import { useCallback } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { CardFormValues, CardPayload } from 'Interfaces/Billing';
import { useCancelPreauthIntent, useCreatePreauthIntent } from '.';
import { Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { isNotEmpty } from 'Utils/functions';
import { useAsyncAction } from 'Hooks/Common';

interface CardDataPayload {
  cardNumber: StripeCardNumberElement;
  billingDetails?: CardFormValues;
}

const successPaymentIntentStatuses = ['requires_capture', 'succeeded'];

export default () => {
  const stripe = useStripe();
  const elements = useElements();
  const [createPreauthIntent] = useCreatePreauthIntent();
  const [cancelPreauthIntent] = useCancelPreauthIntent();

  const handleCancelPreauthIntent = useCallback(
    async (paymentIntentId?: string) => {
      if (paymentIntentId) await cancelPreauthIntent({ paymentIntentId });
    },
    [cancelPreauthIntent],
  );

  const handleConfirmPreauth = useCallback(
    async (clientSecret: string, card: CardDataPayload, stripe: Stripe) => {
      const { cardNumber, billingDetails } = card;

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumber,
          billing_details: {
            name: billingDetails?.cardholderName,
            address: { postal_code: billingDetails?.postalCode },
          },
        },
      });

      if (error) {
        await handleCancelPreauthIntent(error?.payment_intent?.id);
        throw error;
      }

      if (
        !paymentIntent ||
        !successPaymentIntentStatuses.includes(paymentIntent.status)
      ) {
        await handleCancelPreauthIntent(paymentIntent?.id);
        throw new Error('Preauthorization failed');
      }

      return paymentIntent;
    },
    [handleCancelPreauthIntent],
  );

  const handleCardCreate = useCallback(
    async (billingDetails?: CardFormValues): Promise<CardPayload | undefined> => {
      if (!stripe || !elements) return;

      const cardNumber = elements.getElement('cardNumber');

      if (!cardNumber) return;

      const cardPayload = { cardNumber, billingDetails };

      const preauth = await createPreauthIntent(undefined);

      if (!isNotEmpty(preauth) || !preauth.clientSecret) {
        throw new Error('Failed to create preauth intent');
      }

      const paymentIntent = await handleConfirmPreauth(
        preauth.clientSecret,
        cardPayload,
        stripe,
      );

      if (!paymentIntent.payment_method) throw new Error('Payment method missing');

      return {
        token: paymentIntent.payment_method,
        paymentIntentId: paymentIntent.id,
      };
    },
    [stripe, elements, createPreauthIntent, handleConfirmPreauth],
  );

  return useAsyncAction(() => {
    return handleCardCreate();
  });
};
